import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { tierLabel } from './PricingCalc';
import { fmtDate } from '../timefmt';

export default function QuotesView({ toast, canAll, canEdit = true, onEditQuote }) {
  const [quotes, setQuotes] = useState(null);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(null);
  const [ownerQ, setOwnerQ] = useState('');
  const [oppQ, setOppQ] = useState('');
  const [sort, setSort] = useState('date_desc');
  const [loadingMore, setLoadingMore] = useState(false);
  const [viewAll, setViewAll] = useState(canAll && !canEdit); // read-only roles (Executive) land on all quotes

  const params = (offset) => ({
    ...(canAll && viewAll ? { all: 1 } : {}), offset, limit: 50,
    sort: sort === 'date_asc' ? 'date_asc' : 'date_desc',
    ...(canAll && viewAll && ownerQ.trim() ? { owner: ownerQ.trim() } : {}),
    ...(oppQ.trim() ? { opp: oppQ.trim() } : {}),
  });

  useEffect(() => {
    const t = setTimeout(() => {
      api.listQuotes(params(0)).then(d => { setQuotes(d.items || []); setTotal(d.total || 0); }).catch(() => setQuotes([]));
    }, 300);
    return () => clearTimeout(t);
  }, [ownerQ, oppQ, sort, viewAll]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadMore() {
    setLoadingMore(true);
    try {
      const d = await api.listQuotes(params(quotes.length));
      setQuotes(q => [...q, ...(d.items || [])]); setTotal(d.total || 0);
    } catch (e) { toast('Could not load more quotes', true); }
    setLoadingMore(false);
  }

  async function del(id) {
    try {
      await api.deleteQuote(id);
      setQuotes(qs => qs.filter(q => q.id !== id));
      toast('Quote deleted');
    } catch (e) { toast('Delete failed: ' + (e.response?.data?.detail || e.message), true); }
  }

  const fmtN = n => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const shown = (quotes || []).slice().sort((a, b) => {
    if (sort === 'owner_asc') return (a.owner_name || '').localeCompare(b.owner_name || '') || (new Date(b.created_at) - new Date(a.created_at));
    if (sort === 'owner_desc') return (b.owner_name || '').localeCompare(a.owner_name || '') || (new Date(b.created_at) - new Date(a.created_at));
    return 0; // date sorts come pre-sorted from the server
  });

  return (
    <div className="pc-view" data-testid="quotes-view">
      <div className="pc-head">
        <div>
          <h2>Quotes</h2>
          <p className="pc-sub">{canAll && viewAll
            ? 'Every saved quote in this region, one per opportunity.'
            : 'Your saved quotes, one per opportunity.'} Datasheet-matched items sync into a project named after the opportunity number. Push a quote to NetSuite from the Quote Builder — the NS quote number appears here once created.</p>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        {canAll && (
          <select className="select" data-testid="quotes-scope" style={{ maxWidth: 170 }} value={viewAll ? 'all' : 'mine'} onChange={e => setViewAll(e.target.value === 'all')} title="Whose quotes to show">
            <option value="mine">My quotes</option>
            <option value="all">All quotes</option>
          </select>
        )}
        {canAll && viewAll && <input className="input" data-testid="quotes-filter-owner" style={{ maxWidth: 260 }}
          placeholder="Filter by creator (name or email)…" value={ownerQ} onChange={e => setOwnerQ(e.target.value)} />}
        <input className="input" data-testid="quotes-filter-opp" style={{ maxWidth: 220 }}
          placeholder="Search opportunity #…" value={oppQ} onChange={e => setOppQ(e.target.value)} />
        <select className="select" data-testid="quotes-sort" style={{ maxWidth: 200 }} value={sort} onChange={e => setSort(e.target.value)} title="Sort quotes">
          <option value="date_desc">Newest first</option>
          <option value="date_asc">Oldest first</option>
          <option value="owner_asc">Creator A→Z</option>
          <option value="owner_desc">Creator Z→A</option>
        </select>
        {(ownerQ || oppQ) && <span className="field-hint" style={{ alignSelf: 'center' }} data-testid="quotes-filter-count">{shown.length} of {total} matching quotes</span>}
      </div>
      {quotes === null && <div className="empty-note">Loading…</div>}
      {quotes && shown.length === 0 && <div className="empty-note" data-testid="quotes-empty">{(ownerQ || oppQ) ? 'No quotes match those filters.' : 'No quotes yet — build one from the Quote Builder tab.'}</div>}
      {shown.map(q => (
        <div key={q.id} className="pc-saved-item" data-testid={'quote-card-' + q.id}>
          <div className="pc-saved-row">
            <strong>{q.opportunity}</strong>
            {q.owner_name && <span className="quote-owner-pill" data-testid={'quote-owner-' + q.id} title="Created by">{q.owner_name}</span>}
            <span>{fmtDate(q.created_at)} · {q.lines.length} item{q.lines.length !== 1 ? 's' : ''}</span>
            {q.project_id && <span className="pc-ds-pill found" title="Datasheets synced to project">Project: {q.opportunity}</span>}
            {q.ns_quote_number && <a className="pc-ds-pill found" href={q.ns_quote_url} target="_blank" rel="noreferrer" title="Open in NetSuite">NS: {q.ns_quote_number} ↗</a>}
            <span className="strong">{fmtN(q.total)} {q.display_cur}</span>
            <span className="pc-saved-actions">
              <button className="btn btn-sm" data-testid={'quote-open-' + q.id} onClick={() => setOpen(open === q.id ? null : q.id)}>{open === q.id ? 'Hide' : 'View'}</button>
              {canEdit && <button className="btn btn-sm btn-primary" data-testid={'quote-edit-' + q.id} onClick={() => onEditQuote(q)}>Edit in Quote Builder</button>}
              {canEdit && <button className="btn btn-sm btn-danger" data-testid={'quote-del-' + q.id} onClick={() => del(q.id)}>✕</button>}
            </span>
          </div>
          {open === q.id && (
            <table className="pc-quote-table pc-saved-lines">
              <thead>
                <tr><th>Code</th><th>Description</th><th>W/O Notes</th><th>Datasheet</th><th>Pricing</th><th className="pc-qty">Qty</th><th className="num">Unit</th><th className="num">Line total</th></tr>
              </thead>
              <tbody>
                {q.lines.map((ln, i) => (
                  <tr key={i}>
                    <td><strong>{ln.code || '—'}</strong></td>
                    <td className="quote-description-text" data-testid={'saved-quote-description-' + q.id + '-' + i}>{ln.desc}</td>
                    <td>{ln.notes || '—'}</td>
                    <td><span className={'pc-ds-pill ' + (ln.datasheet_id ? 'found' : 'missing')}>{ln.datasheet_id ? '✓ ' + (ln.ds_name || 'Found') : '✕ None'}</span></td>
                    <td>{tierLabel(ln.tier)}</td>
                    <td className="pc-qty">{ln.qty}</td>
                    <td className="num">{fmtN(ln.unit)}</td>
                    <td className="num strong">{fmtN(ln.line_total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="pc-total-row">
                  <td colSpan={7} className="pc-total-label">Quote total ({q.display_cur})</td>
                  <td className="num strong">{fmtN(q.total)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      ))}
      {quotes && quotes.length < total && (
        <button className="btn" data-testid="quotes-load-more" disabled={loadingMore} onClick={loadMore} style={{ margin: '10px 0' }}>
          {loadingMore ? 'Loading…' : `Load more (${quotes.length} of ${total})`}
        </button>
      )}
    </div>
  );
}
